import type {IKarelState} from '../karel/karelInterfaces';

export interface CiPLibraryCallbackResponseStrict {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  pre: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  post: any;
  apiId?: string;
}

export type CiPLibraryCallbackResponse =
  | CiPLibraryCallbackResponseStrict
  | {
      apiId?: string;
    }
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  | void;

export interface CiPLibrary {
  id: string;
  srcPath?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  callback?: (func: string, ...args: any[]) => CiPLibraryCallbackResponse;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  onStep?: (serialization: any) => void;
}

export enum LangId {
  PYTHON = 'python',
  JAVASCRIPT = 'javascript',
  JAVA = 'java',
  CPLUSPLUS = 'cpp',
  HTML = 'html',
  CSS = 'css',
}

/**
 * The ID of a file in the project file system.
 */
export type ProjectFileId = string;

/**
 * The metadata for a file in a project. This allows the project to look up the
 * file by its project file ID and path within the project file system.
 */
export interface ProjectFileData {
  /** The path of the file within the project file system. */
  name: string;
  /** The project file ID of the file. */
  id: ProjectFileId;

  content?: string;
}

/**
 * Structure of the autograding scripts pulled from the Assignment Editor and passed
 * to the pyodide runner
 */
export type GradingScripts = {
  onInput: string;
  onOutput: string;
  onFinish: string;
};


/**
 * Test mode options for code execution.
 * - false: No test mode (normal execution)
 * - 'simple': Basic test mode with line count limiting (100k max lines)
 * - 'advancedAutograder': State machine grading with custom grade_input/grade_output/grade_finish scripts
 */
export type TestMode = false | 'simple' | 'advancedAutograder';





export enum StepVariables {
  LINENO = 'lineno',
  SCOPE_NAME = 'scope_name',
  FILENAME = 'filename',
  API_CALL_POINTER = 'api_call_pointer',
  CANVAS_CALL_POINTER = 'canvas_call_pointer',
  OUTPUT_POINTER = 'output_pointer',
  LOCALS = 'locals',
  OUTPUT = 'output',
  CANVAS_DATA = 'canvas_data',
  API_DATA = 'api_data',
}

/**
 * Step data is the data structure for a single step in the code execution trace.
 * Trace Variables:
 *    - lineno: The current line number being executed.
 *    - scope_name: The name of the current scope (e.g., function name).
 *    - locals: Thde local variables in the current scope.
 *    - outputPtr: The output produced at this step.
 * Api Variables:
 *    - canvasData: Data related to canvas API calls at this step.
 *    - apiData: Object in structure {
 *     [apiId: string]: <state at this step>
 *   }
 *
 *
 */

export type StepData = {
  [StepVariables.LINENO]: number;
  [StepVariables.SCOPE_NAME]: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  [StepVariables.LOCALS]: any;
  [StepVariables.OUTPUT]: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  [StepVariables.CANVAS_DATA]?: any;
  [StepVariables.API_DATA]?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    [apiId: string]: any;
  };
};

/**
 * Per-run, read-only context exposed inside Pyodide via
 * `from js import get_static_data` (see `worker.ts`'s
 * `self.get_static_data`). Each {@link RunCodeOptions.cacheState} value
 * is structured-cloned into the worker on RUN dispatch and replaces the
 * worker's `apiCache` wholesale; there is no in-place mid-run update.
 *
 * Keys split into two groups:
 * - **Caller-supplied**: callers populate these per `runCode` call to
 *   describe the run's context (which project, which Karel world,
 *   etc.). Adding a new caller-supplied key is a contract change for
 *   every consumer that builds a `cacheState` literal.
 * - **Runtime-reserved** (marked `@internal`): WorkerApi composes
 *   these from sources outside the caller's view (e.g. the
 *   {@link GetAuthToken} provider). Callers MUST NOT set these — the
 *   value will be overwritten — and should treat them as a private
 *   protocol between WorkerApi and Pyodide.
 */
export interface CacheState {
    /**
     * Initial Karel world for this run. Read by the karel Pyodide
     * library (`stanfordkarel.py`) at startup. Required for runs that
     * import the karel package; ignored otherwise.
     */
    karelStartState?: IKarelState;
    /**
     * The project whose code is being executed. Embedded in outbound
     * Cloud Function calls (e.g. `call_gpt`) so the server can look up
     * the project's editor and route usage accounting correctly. Set
     * for runs originating from the IDE or share page; absent for
     * synthetic / library-only runs (math4ai, demos).
     */
    projectId?: string;
    /**
     * Pre-configured `call_gpt` answers keyed by the exact prompt string, read
     * by the `ai` Pyodide library via `get_static_data("cannedGptResponses")`.
     * A host with no real project (e.g. a CodeAdventure lesson) supplies these
     * so a known prompt returns a canned answer with no network call. The map
     * is consulted for lesson runs — an absent {@link projectId} or a
     * lesson-prefixed one — and ignored for a real project id, which always
     * makes a live call. Absent for IDE / personal-project runs; a lesson host
     * that has no canned answers may omit it entirely.
     */
    cannedGptResponses?: Record<string, string>;
    /**
     * Absolute URL a learner should visit to ask staff for more AI budget,
     * read by the `ai` Pyodide library via `get_static_data("reportUrl")`
     * when a `call_gpt` call is refused for an exhausted allowance.
     *
     * Supplied by the host rather than built in the wheel (which would have
     * to hardcode a webapp route) or by the server (which receives only a
     * project id, so it cannot know the course). Hosts with no course to
     * point at — math4ai, the code-runner demos — omit it, and the raised
     * message falls back to generic "contact your course staff" wording.
     */
    reportUrl?: string;
    /**
     * Whether the run uses the graphics library. Read by the worker to
     * decide whether to wire up the offscreen canvas channel. Currently
     * not set by any caller via `cacheState` — flows through the
     * library registration path instead — but the worker reads from
     * here so we keep the slot reserved.
     */
    isGraphics?: boolean;
    /**
     * @internal Runtime-reserved. Set by WorkerApi from the host's
     * {@link GetAuthToken} provider, only on runs that set
     * {@link RunCodeOptions.attachAuthToken}. Forwarded as
     * `Authorization: Bearer <token>` by Pyodide libraries that POST to
     * Cloud Functions (`call_gpt`). Callers MUST NOT populate this key
     * directly; any caller-provided value is overwritten on dispatch.
     */
    idToken?: string;
    /**
     * @internal Runtime-reserved. Cloud Functions base URL (no
     * trailing slash, no function name) injected by WorkerApi from
     * the host's `functionsBaseUrl` option. Pyodide libraries
     * (`call_gpt`) read this and append the function name so the
     * same wheel can target production or a running Functions
     * emulator. Callers MUST NOT populate this key directly; any
     * caller-provided value is stripped on dispatch.
     */
    functionsBaseUrl?: string;
}

export interface RunCodeOptions {
    files?: ProjectFileData[]
    stepMode?: boolean;
    testMode?: TestMode;
    /**
     * Max lines the tracer lets a program execute in test mode before it
     * raises `KeyboardInterrupt("Interrupted by tests")` — the runaway-code
     * guard for autograding. Omitted defaults to 100_000 (worker-side); the
     * mathide/function-call autograder raises it for compute-heavy assignments
     * (e.g. PAI's MNIST/ML). Only meaningful when `testMode` is set.
     */
    testLineCount?: number;
    gradingScripts?: GradingScripts;
    /**
     * Caller-supplied per-run context. WorkerApi spread-merges this
     * with any runtime-reserved keys (`idToken`, `functionsBaseUrl`)
     * to compose the wire payload; the caller's object is treated as
     * read-only and is never mutated. See {@link CacheState}.
     */
    cacheState?: CacheState;
    /**
     * Attach the host's auth token (see `BasePlugOptions.getAuthToken`) to
     * this run as `cacheState.idToken`. The running code can read it, so set
     * this only for code the signed-in viewer wrote or staff authored, never
     * for another user's code. Omitted or false, the run carries no token.
     */
    attachAuthToken?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    imageCache?: any;
    isGraphics?: boolean;
    replMode?: boolean;
    /**
     * Integer seed for Python's stdlib `random` module, applied before the
     * student code runs. Omit for default time/OS-entropy seeding.
     */
    randomSeed?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    apiOverrides?: {[key: string]: (func: string, ...args: any[]) => CiPLibraryCallbackResponse;};
    ioOverrides?: {
        input?: () => Promise<string>;
        output?: (output: string) => void;
        error?: (error: string) => void;
        onRunEnd?: () => void;
        onStep?: (data: StepData) => void;
    };
}

/**
 * State returned from the advanced autograder after execution.
 */
export interface AdvancedAutograderState {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    registeredVars: Map<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    errorState: Map<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    autogradeTrace: Array<Map<string, any>>;
    stopRequested: boolean;
}

export interface RunResult {
    error: string[];
    output: string[];
    traceData?: StepData[];
    fromTests?: boolean;
    advancedAutograderState?: AdvancedAutograderState;
    /**
     * True when the run was halted by `interruptCode` (user-initiated Stop or
     * a stdin-time interrupt) rather than completing naturally. Consumers can
     * use this to skip post-run pipeline steps (e.g. unit-test phases) that
     * the user intended to abort.
     */
    interrupted?: boolean;
}
